import React, { useState } from 'react';
import { usePendingApprovals } from '../../../hooks/usePartnerData';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PendingApprovals = () => {
  const { approvals, loading, error, approvePartner, rejectPartner } = usePendingApprovals();
  const [actionLoading, setActionLoading] = useState({});

  const handleApprove = async (partnerId) => {
    setActionLoading(prev => ({ ...prev, [`approve-${partnerId}`]: true }));
    try {
      await approvePartner(partnerId);
    } catch (error) {
      // Error handled by hook
    } finally {
      setActionLoading(prev => ({ ...prev, [`approve-${partnerId}`]: false }));
    }
  };

  const handleReject = async (partnerId) => {
    setActionLoading(prev => ({ ...prev, [`reject-${partnerId}`]: true }));
    try {
      await rejectPartner(partnerId);
    } catch (error) {
      // Error handled by hook
    } finally {
      setActionLoading(prev => ({ ...prev, [`reject-${partnerId}`]: false }));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short'
    });
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-card-foreground mb-4">Approbations en Attente</h3>
        <div className="space-y-3">
          {[1, 2, 3]?.map(i => (
            <div key={i} className="animate-pulse">
              <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-muted rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-card-foreground mb-4">Approbations en Attente</h3>
        <div className="text-center text-muted-foreground py-8">
          <Icon name="AlertCircle" size={32} className="mx-auto mb-2" />
          <p>Erreur de chargement des approbations</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-card-foreground">Approbations en Attente</h3>
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-warning rounded-full animate-pulse"></div>
          <span className="text-sm text-muted-foreground">{approvals?.length || 0} en attente</span>
        </div>
      </div>
      <div className="space-y-4">
        {approvals?.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            <Icon name="CheckCircle" size={32} className="mx-auto mb-2 text-green-500" />
            <p>Aucune approbation en attente</p>
          </div>
        ) : (
          approvals?.map((partner) => (
            <div key={partner?.id} className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors">
              <div className="flex-1">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center">
                    <Icon name="User" size={16} className="text-accent" />
                  </div>
                  <div>
                    <h4 className="font-medium text-card-foreground">{partner?.business_name}</h4>
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <span>{partner?.email}</span>
                      <span>•</span>
                      <span className="capitalize">{partner?.partner_type}</span>
                      <span>•</span>
                      <span>{formatDate(partner?.created_at)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleReject(partner?.id)}
                  disabled={actionLoading?.[`reject-${partner?.id}`] || actionLoading?.[`approve-${partner?.id}`]}
                  loading={actionLoading?.[`reject-${partner?.id}`]}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  <Icon name="X" size={14} className="mr-1" />
                  Rejeter
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleApprove(partner?.id)}
                  disabled={actionLoading?.[`reject-${partner?.id}`] || actionLoading?.[`approve-${partner?.id}`]}
                  loading={actionLoading?.[`approve-${partner?.id}`]}
                >
                  <Icon name="Check" size={14} className="mr-1" />
                  Approuver
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PendingApprovals;